import { Injectable } from '@angular/core';
import { EsriService } from './esri.service';
import { UserService } from './user.service';
import { StartupInfo } from '../model/startup-info.model';
import { TranslateService } from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
import { ConfigUtilsService } from '../../config/config-utils.service';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class StartupService
 * @version: 0.0.1
 * @since September 2017
 * @description The StartupService handles the works before the app is started.
 */
@Injectable({
  providedIn: 'root',
})
export class StartupService {
  private _startupInfoList: StartupInfo[];
  private _loadEsriAPI: StartupInfo = new StartupInfo();
  private _loadUser: StartupInfo = new StartupInfo();
  public isStarted = false;
  public isStarting = false;
  public hasFailed = false;
  public errors: string[] = [];

  constructor(
    private translate: TranslateService,
    private esriService: EsriService,
    private userService: UserService,
    private configUtilsService: ConfigUtilsService
  ) {
    this.translate
      .get(['STARTUP.LOADING_COMPONENTS', 'STARTUP.INITIAL_USER'])
      .subscribe(labels => {
        this._loadEsriAPI.setTitle(labels['STARTUP.LOADING_COMPONENTS']);
        this._loadUser.setTitle(labels['STARTUP.INITIAL_USER']);
      });

    this._startupInfoList = [this._loadUser, this._loadEsriAPI];
  }

  /**
   * @name start
   * @returns {Promise<any>}
   * @description The start process loads all needed resources from
   * different endpoints :
   *    - ESRI API
   *    - The context profile from logged user
   */
  public async start(): Promise<any> {
    // set UI-flag if Esri API loading begins to start
    this.isStarting = true;
    this._loadEsriAPI.start();
    this.hasFailed = false;
    this.errors = [];
    try {
      // load Esri API
      this.esriService.init();
      this._loadEsriAPI.done();
      this._loadUser.start();
      // get user infos
      const fullfilledUserInfo = await this.userService.getUserInfo();
      // loading user info successfully done
      this._loadUser.done();

      // check configs if user have just viewer role to switch services
      this.configUtilsService.validateLayerSwitchForViewer();

      // if every services has been started, return true
      if (fullfilledUserInfo.status === 200) {
        this.isStarted = true;

        // create own status for user login success
        const successRes = new HttpResponse({
          status: 200,
          statusText:
            'StartupService.start: successfully loading dependancies Esri API and UserInfo',
        });
        return successRes;
      } else {
        if (fullfilledUserInfo.status !== 200) {
          this.errors.push(fullfilledUserInfo.statusText);
          throw fullfilledUserInfo;
        }
      }
    } catch (error) {
      this.hasFailed = true;
      return error;
    } finally {
      this.isStarting = false;
    }
  }

  /**
   * @name startupInfoList
   * @returns {StartupInfo[]}
   * @description List of startup information
   */
  public get startupInfoList(): StartupInfo[] {
    return this._startupInfoList;
  }
}
